import { Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';

import { validateInvoiceDates, validateInvoiceIds } from '../invoice-list-component.validation';
import { PharmacyAutoComplete } from '../shared/pharmacy-auto-complete/pharmacy-auto-complete';
import { FilterMultiSelectComponent } from '../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterSortingComponent } from '../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { FilterDateComponent } from '../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterItemSearch } from '../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { InvoiceFiltersModel } from './Invoice-filters.model';
import { PharmacyAutoCompleteApiItem } from '../../../../services/pharmace-auto-complete/pharmace-auto-complete.service';

@Component({
  selector: 'app-invoice-filters',
  standalone: true,
  imports: [
    FilterItemSearch,
    FilterDateComponent,
    SubmitButton,
    FilterSortingComponent,
    FilterMultiSelectComponent,
    PharmacyAutoComplete,
  ],
  templateUrl: './invoice-filters.html',
})
export class InvoiceFilters {
  private locale = inject(LOCALE_ID);

  filters = input.required<InvoiceFiltersModel>();
  statusOptions = input<{ id: string; titleEn: string; titleAr: string }[]>([]);
  applyFilters = output<InvoiceFiltersModel>();
  resetFilters = output();
  toggleSortOrder = output();
  selectPharmacy = output<PharmacyAutoCompleteApiItem>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  invoiceIdFilter = $localize`:@@invoiceIdFilter:Invoice ID`;
  invoiceIdPlaceholder = $localize`:@@invoiceIdPlaceholder:Search by Invoice ID`;
  orderIdFilter = $localize`:@@orderIdFilter:Order ID`;
  orderIdPlaceholder = $localize`:@@orderIdPlaceholder:Search by Order ID`;
  pharmacyLabel = $localize`:@@pharmacyLabel:Pharmacy`;
  pharmacyPlaceholder = $localize`:@@pharmacyPlaceholder:Search by pharmacy or owner...`;
  noPharmacyResults = $localize`:@@noPharmacyResults:No matching results`;
  invoiceDateFrom = $localize`:@@invoiceDateFrom:Invoice Date From`;
  invoiceDateTo = $localize`:@@invoiceDateTo:Invoice Date To`;

  statusFilter = signal({ titleEn: 'Status', titleAr: 'الحالة' });
  allStatuses = signal({ titleEn: 'Select Status', titleAr: 'حدد الحالة' });

  sortOptions = signal([
    { value: 'INVOICE_DATE', labelEn: 'Invoice Date', labelAr: 'تاريخ الفاتورة' },
    { value: 'TOTAL_PHARMACY_PRICE', labelEn: 'Total Pharmacy Price', labelAr: 'إجمالي سعر الصيدلية' },
    { value: 'INVOICE_STATUS', labelEn: 'Status', labelAr: 'الحالة' },
  ]);

  invoiceSearch = signal('');
  orderSearch = signal('');
  invoiceIdError = signal<string | null>(null);
  orderIdError = signal<string | null>(null);
  selectedStatuses = signal<string[]>([]);
  showStatusDropdown = signal(false);

  selectedPharmacy = signal<PharmacyAutoCompleteApiItem | null>(null);
  pharmacyClear = signal(0);

  invoiceFrom = signal<string | null>(null);
  invoiceTo = signal<string | null>(null);
  fromDateError = signal<string | null>(null);
  toDateError = signal<string | null>(null);

  toggleStatusDropdown() {
    this.showStatusDropdown.update((value) => !value);
  }

  onStatusChange(statuses: string[]) {
    this.selectedStatuses.set(statuses);
  }

  onFromDateChange(value: string | null) {
    this.invoiceFrom.set(value);
    this.validateDates();
  }

  onToDateChange(value: string | null) {
    this.invoiceTo.set(value);
    this.validateDates();
  }

  onPharmacySelected(selected: PharmacyAutoCompleteApiItem) {
    this.selectedPharmacy.set(selected);
    this.selectPharmacy.emit(selected);
  }

  private validateDates(): boolean {
    const result = validateInvoiceDates(this.invoiceFrom(), this.invoiceTo(), this.isArabic());
    this.fromDateError.set(result.fromDateError);
    this.toDateError.set(result.toDateError);
    return result.isValid;
  }

  private validateIds(): boolean {
    const result = validateInvoiceIds(this.invoiceSearch().trim(), this.orderSearch().trim(), this.isArabic());
    this.invoiceIdError.set(result.invoiceIdError);
    this.orderIdError.set(result.orderIdError);
    return result.isValid;
  }

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  onResetFilters() {
    this.invoiceSearch.set('');
    this.orderSearch.set('');
    this.invoiceIdError.set(null);
    this.orderIdError.set(null);
    this.selectedStatuses.set([]);
    this.selectedPharmacy.set(null);
    this.pharmacyClear.update((value) => value + 1);
    this.invoiceFrom.set(null);
    this.invoiceTo.set(null);
    this.fromDateError.set(null);
    this.toDateError.set(null);
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const idsValid = this.validateIds();
    const datesValid = this.validateDates();
    if (!idsValid || !datesValid) return;

    const invoiceId = this.invoiceSearch().trim();
    const orderId = this.orderSearch().trim();

    const firstStatusId = this.selectedStatuses()[0];
    const requestStatusId = firstStatusId ? Number(firstStatusId) : undefined;

    this.applyFilters.emit({
      invoiceId: invoiceId ? Number(invoiceId) : undefined,
      orderId: orderId ? Number(orderId) : undefined,
      pharmacyId: this.selectedPharmacy()?.id ? Number(this.selectedPharmacy()!.id) : undefined,
      requestStatusId,
      invoiceFrom: this.invoiceFrom() ?? undefined,
      invoiceTo: this.invoiceTo() ?? undefined,
    });
  }
}
